import { useMemo, useState } from "react";
import { MoreHorizontal, Pencil, Trash2, MessageSquare, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
  type CopilotConversation,
} from "@/hooks/useCopilotConversations";
import { getTierBadgeLabel } from "@/components/admin/UpgradeGate";

interface ChatHistoryListProps {
  activeId: string | null;
  onSelect: (id: string) => void;
  collapsed?: boolean;
  locked?: boolean;
  requiredTier?: "professional" | "enterprise" | "enterprise_plus" | null;
}

const bucketOf = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  if (d >= today) return "Today";
  if (d >= yesterday) return "Yesterday";
  if (d >= sevenDaysAgo) return "Last 7 days";
  return "Older";
};

const ORDER = ["Today", "Yesterday", "Last 7 days", "Older"];

export const ChatHistoryList = ({
  activeId,
  onSelect,
  collapsed,
  locked,
  requiredTier,
}: ChatHistoryListProps) => {
  const { data: conversations = [], isLoading } = useConversations(!locked);
  const renameMut = useRenameConversation();
  const deleteMut = useDeleteConversation();

  const [renaming, setRenaming] = useState<CopilotConversation | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<CopilotConversation | null>(null);

  const grouped = useMemo(() => {
    const buckets: Record<string, CopilotConversation[]> = {};
    for (const c of conversations) {
      const b = bucketOf(c.updated_at);
      (buckets[b] ||= []).push(c);
    }
    return ORDER.filter((k) => buckets[k]?.length).map((k) => ({ label: k, items: buckets[k] }));
  }, [conversations]);

  if (collapsed) return null;

  if (locked) {
    return (
      <div className="px-3 py-4 text-xs text-muted-foreground flex items-center gap-2">
        <Lock className="h-3 w-3" />
        <span>Chat history</span>
        {requiredTier && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
            {getTierBadgeLabel(requiredTier)}
          </Badge>
        )}
      </div>
    );
  }

  if (isLoading) {
    return <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>;
  }

  if (conversations.length === 0) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground italic">No conversations yet</div>
    );
  }

  return (
    <div className="space-y-3">
      {grouped.map((group) => (
        <div key={group.label}>
          <p className="text-[10px] font-medium text-muted-foreground/70 px-3 mb-1 uppercase tracking-wider">
            {group.label}
          </p>
          <div className="space-y-0.5">
            {group.items.map((c) => (
              <div
                key={c.id}
                className={cn(
                  "group/item flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-colors",
                  activeId === c.id
                    ? "bg-primary/10 text-foreground"
                    : "hover:bg-secondary text-muted-foreground hover:text-foreground"
                )}
                onClick={() => onSelect(c.id)}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate">{c.title}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover/item:opacity-100 shrink-0"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameValue(c.title);
                        setRenaming(c);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setConfirmDelete(c)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Rename dialog */}
      <AlertDialog open={!!renaming} onOpenChange={(open) => !open && setRenaming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename conversation</AlertDialogTitle>
          </AlertDialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            maxLength={80}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (renaming && renameValue.trim()) {
                  await renameMut.mutateAsync({ id: renaming.id, title: renameValue.trim() });
                }
                setRenaming(null);
              }}
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{confirmDelete?.title}" and all its messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (confirmDelete) await deleteMut.mutateAsync(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
